import React, { Fragment, useState, useCallback, useEffect } from 'react';
import { GetList, AddItem, DeleteItem, UpdateItem } from '../../REST/crud';
import { useForm, Controller, useFormState } from "react-hook-form";
import TextField from '@mui/material/TextField';
import NativeSelect from '@mui/material/NativeSelect';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import FormHelperText from '@mui/material/FormHelperText';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from "yup";
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import PageShell from '../../themes/components/PageShell';
import PageHeader from '../../themes/components/PageHeader';
import PageContent from '../../themes/components/PageContent';
import Action from '../../themes/components/Action';
import ResponsiveDataGrid from '../../themes/components/ResponsiveDataGrid';
import WorkflowDialogShell from '../../themes/components/WorkflowDialogShell';

export default function PaymentTypes() {
    const [mode, setMode] = useState(null)
    const [refresh, setRefresh] = useState(false)
    const navigate = useNavigate()

    const columns = [
        { field: 'name', headerName: 'Name', flex: 0.5 },
        { field: 'description', headerName: 'Description', flex: 0.4 },
        {
            field: 'actions', headerName: 'Actions', flex: 0.1, renderCell: (params) => {
                return <IconButton onClick={(e) => { e.stopPropagation(); handleDelete(params.row._id) }}><DeleteIcon /></IconButton>
            }
        }
    ];

    const fetchPaymentTypes = useCallback(async (page, limit, filters, sort) => {
        const params = {
            page,
            limit
        }
        
        if (Object.keys(filters).length > 0) {
            params.filters = JSON.stringify(filters)
        }
        
        if (sort) {
            params.sort = JSON.stringify(sort)
        }
        
        const result = await GetList('SkuType', params);

        if (result.data?.data) {
            return {
                data: result.data.data,
                pagination: result.data.pagination
            };
        }

        if (Array.isArray(result.data)) {
            return {
                data: result.data,
                pagination: { total: result.data.length, page: 1, totalPages: 1 }
            };
        }

        return { data: [], pagination: { total: 0, page: 1, totalPages: 0 } };
    }, []);

    const handleMode = (mode) => { setMode(mode) }

    const handleData = (data) => {
        const { _id, ...fields } = data
        const action = _id ? UpdateItem('SkuType', _id, fields) : AddItem('SkuType', fields)
        action.then(() => {
            setMode(null)
            setRefresh(r => !r)
        })
    }

    const handleDelete = (id) => {
        DeleteItem('SkuType', id).then(() => {
            setRefresh(r => !r)
        })
    }

    const handleRowClick = (row) => {
        setMode(row)
    }

    return <Fragment>
        <PageShell>
            <PageHeader
                title="Payment Types"
                back
                onBack={() => navigate(-1)}
                actions={
                    <Action
                        primary
                        icon={<AddIcon />}
                        label="Add Payment Type"
                        onClick={() => handleMode({})}
                    />
                }
            />

            {mode !== null && <FormDialog open={mode !== null} data={mode} handleClose={() => handleMode(null)} handleSave={handleData} />}

            <PageContent fill>
                <ResponsiveDataGrid
                    key={refresh}
                    columns={columns}
                    fetchData={fetchPaymentTypes}
                    initialPageSize={10}
                    pageSizeOptions={[10, 25, 50, 100]}
                    getRowId={(i) => i?._id}
                    onRowClick={handleRowClick}
                    sx={{ cursor: 'pointer' }}
                />
            </PageContent>
        </PageShell>
    </Fragment>
}



function FormDialog({ open = false, data, handleClose, handleSave }) {
    const isEdit = !!data?._id

    const schema = yup.object({
        name:        yup.string().trim().required('Name is required'),
        description: yup.string().trim().required('Description is required'),
        skuType:     yup.string().trim().required('Type is required'),
        skuValue:    yup.string().trim().required('Value is required'),
        lateFeeType: yup.string().trim().required('Late Fee Type is required'),
        lateFee:     yup.number().typeError('Must be a number').required('Late Fee is required')
    }).required()

    const { control, handleSubmit, formState: { errors }, clearErrors, reset, watch } = useForm({
        defaultValues: {
            name:        '',
            description: '',
            skuType:     '',
            skuValue:    '',
            lateFeeType: 'Percent',
            lateFee:     0
        },
        resolver: yupResolver(schema)
    })

    const { isDirty } = useFormState({ control })

    useEffect(() => {
        clearErrors()
        reset({
            name:        data?.name        ?? '',
            description: data?.description ?? '',
            skuType:     data?.skuType     ?? '',
            skuValue:    data?.skuValue    ?? '',
            lateFeeType: data?.lateFeeType ?? 'Percent',
            lateFee:     data?.lateFee     ?? 0,
            _id:         data?._id         ?? undefined
        })
    }, [open, data, clearErrors, reset])

    const skuTypeVal = watch('skuType')
    const lateFeeTypeVal = watch('lateFeeType')

    const getLabel = (val) => {
        if (val === 'CPI')     return 'CPI Rate (decimal, e.g. 0.03 for 3%)'
        if (val === 'Flat')    return 'Flat Amount ($ added per term)'
        if (val === 'Percent') return 'Percentage (e.g. 10 for 10%)'
        return 'SKU Value'
    }

    const isError = (val) => val !== undefined && val !== null && typeof val === 'object'

    const onSubmit = (formData) => {
        if (isEdit) formData._id = data._id
        handleSave(formData)
    }

    return <WorkflowDialogShell
        open={open}
        onClose={handleClose}
        onSave={handleSubmit(onSubmit)}
        title={isEdit ? 'Edit Payment Type' : 'Add Payment Type'}
    >
        <>
            <Controller name="name" control={control} render={({ field }) =>
                <TextField margin='dense' fullWidth required label='Name'
                    error={isError(errors.name)} helperText={errors.name?.message} {...field} />} />

            <Controller name="description" control={control} render={({ field }) =>
                <TextField margin='dense' fullWidth required label='Description'
                    error={isError(errors.description)} helperText={errors.description?.message} {...field} />} />

            <Controller name="skuType" control={control} render={({ field }) =>
                <FormControl error={isError(errors.skuType)} variant='standard' margin='dense' fullWidth required>
                    <InputLabel>Rent Adjustment Type</InputLabel>
                    <NativeSelect {...field}>
                        <option value=''>Please Select</option>
                        <option value='CPI'>CPI Based</option>
                        <option value='Flat'>Flat Rate</option>
                        <option value='Percent'>Percent Based</option>
                    </NativeSelect>
                    {errors.skuType && <FormHelperText>{errors.skuType.message}</FormHelperText>}
                </FormControl>} />

            <Controller name="skuValue" control={control} render={({ field }) =>
                <TextField margin='dense' fullWidth required label={getLabel(skuTypeVal)}
                    error={isError(errors.skuValue)} helperText={errors.skuValue?.message} {...field} />} />

            <Controller name="lateFeeType" control={control} render={({ field }) =>
                <FormControl error={isError(errors.lateFeeType)} variant='standard' margin='dense' fullWidth required>
                    <InputLabel>Late Fee Type</InputLabel>
                    <NativeSelect {...field}>
                        <option value='Percent'>Percentage Based</option>
                        <option value='Flat'>Flat Amount</option>
                    </NativeSelect>
                    {errors.lateFeeType && <FormHelperText>{errors.lateFeeType.message}</FormHelperText>}
                </FormControl>} />

            <Controller name="lateFee" control={control} render={({ field }) =>
                <TextField type='number' margin='dense' fullWidth required
                    label={lateFeeTypeVal === 'Flat' ? 'Late Fee ($ flat amount)' : 'Late Fee (%)'}
                    error={isError(errors.lateFee)} helperText={errors.lateFee?.message} {...field} />} />
        </>
    </WorkflowDialogShell>
}
